import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, Column, Row } from "react-table";

import { LocationDto } from "@apiModels/LocationDto.csharp";
import { DoctorsApi } from "../../../Api/DoctorsApi";

import { DoctorsListHeadings } from "./Components/DoctorsListHeadings";
import { Button } from "../../Partials/Button";
import { Placeholder } from "../../Partials/Placeholder";

import { useInsuranceFilters } from "../../Utils/Context/filtersContext";
import { useInsuranceLabels } from "../../Utils/Context/labelsContext";
import { useInsuranceKeywords } from "../../Utils/Context/keywordsContext";

import { useFilteredDoctors } from "../../Utils/Hooks/useFilteredDoctors";
import { useAppointments } from "../../Utils/Hooks/useAppointments";
import { mapFiltersToRequest } from "../../Utils/Filters/Utils";

import { DoctorBar, DoctorsListModel } from "../DoctorBar/DoctorBar";
import { mapDoctorsToAppointment } from "./Utils/mapDoctorsToAppointment";
import { sortDoctorsByDate } from "./Utils/sortDoctorsByDate";
import { DoctorsListPlaceholder } from "./Components/DoctorsListPlaceholder";
import { InsuranceDoctorDto } from "@apiModels/Insurance/InsuranceDoctorDto.csharp";

const BASE_DOCTORS_LIMIT = 6;
const BASE_DOCTORS_PLACEHOLDERS = 8;

interface DoctorsListParams {
  locations: LocationDto[];
  isAuthenticated: boolean;
  getFavorites?: boolean;
}

export const DoctorsList: React.FC<DoctorsListParams> = (props) => {
  const { locations, isAuthenticated, getFavorites = false } = props;

  const filters = useInsuranceFilters();
  const { setKeywords } = useInsuranceKeywords();
  const { headings, buttons } = useInsuranceLabels();

  const {
    isLoading: doctorsLoading,
    doctors,
    keywords,
  } = useFilteredDoctors(filters, getFavorites);
  const {
    isLoading: appointmentsLoading,
    appointments,
    price,
  } = useAppointments(filters.service);

  const [doctorsLimit, setDoctorsLimit] = useState(BASE_DOCTORS_LIMIT);

  useEffect(() => {
    setDoctorsLimit(BASE_DOCTORS_LIMIT);
  }, [doctors]);

  useEffect(() => {
    setKeywords(keywords);
  }, [keywords]);

  const data = useMemo<DoctorsListModel[]>(
    () =>
      doctors
        .slice(0, doctorsLimit)
        .map((doctor) =>
          mapDoctorsToAppointment(
            doctor,
            appointments,
            price,
            locations,
            filters
          )
        ),
    [
      doctors,
      doctors.length,
      doctorsLimit,
      appointments,
      filters.locations,
      price,
    ]
  );

  const columns = useMemo<Column<DoctorsListModel>[]>(
    () => [
      {
        Header: headings.specialistHeading,
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: headings.departmentHeading,
        accessor: "department",
        sortType: () => {},
      },
      {
        Header: headings.locationHeading,
        accessor: "centers",
        disableSortBy: true,
      },
      {
        Header: headings.servicePriceHeading,
        accessor: "price",
        disableSortBy: true,
      },
      {
        Header: headings.availableDateHeading,
        accessor: "date",
        sortType: sortDoctorsByDate,
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useSortBy
    );

  const getLocations = (doctor: InsuranceDoctorDto) =>
    locations.filter((loc) => doctor.centers.includes(loc.id));

  const renderTableRows = (row: Row<InsuranceDoctorDto>) => {
    prepareRow(row);
    const { id, original: doctor } = row;

    const barStatus = appointmentsLoading
      ? "loading"
      : filters.service && filters.specialization
      ? "withFilters"
      : "noFilters";

    const urlParameters = DoctorsApi.mapRequestToQueryUrl(
      mapFiltersToRequest(filters)
    );

    return (
      <tr className="doctorsList__row" key={id}>
        <td className="doctorsList__item" colSpan={5}>
          <DoctorBar
            doctor={doctor}
            locations={getLocations(doctor)}
            isAuthenticated={isAuthenticated}
            barStatus={barStatus}
            doctorUrl={`${doctor.insuranceTreatedUrl}&${urlParameters}`}
          />
        </td>
      </tr>
    );
  };

  const renderPlaceholder = () =>
    Array(BASE_DOCTORS_PLACEHOLDERS)
      .fill("")
      .map((_, key) => <DoctorsListPlaceholder key={key} />);

  return (
    <div className="doctorsList">
      <table className="doctorsList__table" {...getTableProps()}>
        {headerGroups.map((headerGroup, index) => (
          <DoctorsListHeadings key={index} headerGroup={headerGroup} />
        ))}
        <tbody {...getTableBodyProps()}>
          <Placeholder
            custom={() => renderPlaceholder()}
            className="doctorsList__placeholder"
            loadingDone={!doctorsLoading}
            type="media"
          >
            {rows.length > 0
              ? rows.map(renderTableRows)
              : !doctorsLoading && (
                  <tr className="doctorsList__row">
                    <td className="doctorsList__item" colSpan={5}>
                      <div className="doctorsList__noData">
                        {"No data available... Please change filters"}
                      </div>
                    </td>
                  </tr>
                )}
          </Placeholder>
        </tbody>
      </table>
      {doctors.length > doctorsLimit && !doctorsLoading && (
        <Button
          onClick={() => setDoctorsLimit(doctorsLimit + BASE_DOCTORS_LIMIT)}
          className="doctorsList__loadMoreButton"
          type="solid"
          tier="secondary"
          size="medium"
        >
          {buttons.seeMoreButtonLabel}
        </Button>
      )}
    </div>
  );
};
